import React from "react";
import {useEffect, useState} from "react";
import {changePageSizeState} from "../../../Slices/PageSlice";
import PageSizeState from "../../../Constants/Base/PageSizeState";
import {useDispatch} from "react-redux";
import TablePage from "../../../Components/DataGrid/TablePage";
import strings from "../../../localization";
import {changeBreadcrumbs} from "../../../Slices/BreadcrumbsSlice";
import Modules from "../../../Constants/Base/Modules";
import TablePageContext, {
    FilterDefaults,
    TableDataDefaults,
    TablePageOptionDefaults
} from "../../../Context/TablePageContext";
import PageState from "../../../Constants/Base/PageState";
import {deleteProjectResources, getProjectResources} from "../../../Services/Project/ProjectResourceService";
import AddProjectResource from "./AddProjectResource";
import {formatColumnDate, formatColumnObject} from "../../../Components/DataGrid/ValueFormatter";
import EditProjectResouce from "./EditProjectResouce";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReferenceType from "../../../Constants/DocumentManagement/ReferenceType";
import { renderColumnNumber } from "../../../Components/DataGrid/ValueCellRender";
import AppPermissions from "../../../Constants/Permissions/Permissions";
import {ResourceTypeNames} from "../../../Constants/Project/ProjectResourceType";
import Tag from "../../../Components/Forms/Pages/Document/Tag/Tag";

const nameFormat = params => formatColumnObject(params, 'name')

const tableDescription = [
    {field: 'name', headerName: strings.pages.project.projectResource.projectResourceList.name, width: 250},
    {field: 'price', headerName: strings.pages.project.projectResource.projectResourceList.price, renderCell: params => renderColumnNumber(params), width: 150},
    {field: 'date', headerName: strings.pages.project.projectResource.projectResourceList.date, valueFormatter: params => formatColumnDate(params), width: 150},
    {field: 'currency', headerName: strings.pages.project.projectResource.projectResourceList.currency, valueFormatter: nameFormat, width: 150},
    {
        field: 'type', 
        headerName: strings.pages.project.projectResource.projectResourceList.type, 
        renderCell: (params) => {
            if(ResourceTypeNames[params.row.type]){
                return ResourceTypeNames[params.row.type];
            }

            return '';
        },
        width: 150
    },
    {
        field: 'invoice', 
        headerName: strings.pages.project.projectResource.projectResourceList.invoice,
        valueGetter: (params) => params.row?.invoice?.invoiceNumber,
        width: 150
    },
    {field: 'project', headerName: strings.pages.project.projectResource.projectResourceList.project, valueFormatter: nameFormat, width: 150},
    {field: 'projectResourceType', headerName: strings.pages.project.projectResource.projectResourceList.projectResourceType, valueFormatter: nameFormat, width: 200},
];

const filters = []

const ProjectResourceList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [updatedFilter, setUpdatedFilter] = useState(!Boolean(searchParams.toString()));
    const [columns, setColumns] = useState([]);
    const [pageState, setPageState] = useState(PageState.View);
    const [filter, setFilter] = useState(FilterDefaults);
    const [tableData, setTableData] = useState(TableDataDefaults);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [selectionModel, setSelectionModel] = useState([]);
    const [selectedItems, setSelectedItems] = useState(null);
    const [tablePageOptions, setTablePageOptions] = useState(TablePageOptionDefaults);
    const referenceType = ReferenceType.PROJECT_RESOURCE;
    const permissionGroup = AppPermissions.PROJECT_RESOURCE.GROUP;

    const value = {
        tablePageOptions, setTablePageOptions,
        selectionModel, setSelectionModel,
        selectedItems, setSelectedItems,
        filter, setFilter, pageState, setPageState, showDeleteDialog, setShowDeleteDialog,
        navigate, updatedFilter, setUpdatedFilter
    }

    useEffect(() => {
        setColumns([...tableDescription]);
        dispatch(changePageSizeState(PageSizeState.SHORT));
        dispatch(changeBreadcrumbs({
            title: strings.pages.project.projectResource.projectResourceList.pageTitle,
            hierarchy:[
                {label: strings.navigation.managmentTag},
                {label: Modules.PROJECTS},
                {label: strings.pages.project.projectResource.projectResourceList.pageTitle},
            ],        
        }));

        return () => {
            setTableData({})
        }
    }, [])

    useEffect(() => {
        if(!updatedFilter){
            return;
        }

        fetch();
    }, [filter, updatedFilter])

    const onCancel = () => {
        setPageState(PageState.View);
    }

    const onFinish = () => {
        fetch();
        setPageState(PageState.View);
    }

    const fetch = () => {
        setTableData({
            ...tableData,
            loading: true
        });

        getProjectResources({
            ...filter
        }).then(response => {

            if (!response || !response.ok) {
                return;
            }

            setTableData({
                loading: false,
                data: response.data.result,
                total: response.data.total
            });
        })
    }

    return <TablePageContext.Provider value={value}>
        <TablePage onFinish={() => onFinish()} deleteItem={deleteProjectResources} tableDescription={columns}
                   tableData={tableData} filter={filter} filters={filters}
                   referenceType={referenceType} permissionGroup={permissionGroup}
                   tagPage={<Tag referenceType={ referenceType } data={ selectedItems } /> }
                   editPage={<EditProjectResouce data={ selectedItems }
                                          onCancel={() => onCancel()} onFinish={() => onFinish()}/>}
                   addPage={<AddProjectResource onCancel={() => onCancel()} onFinish={() => onFinish()}/>}
                   addButtonText={strings.components.tablePage.addButton.projectResource}/>
    </TablePageContext.Provider>;
}

export default ProjectResourceList;
